import amd_stats from "./amd_stats";
import {
  ConfigurationException,
  DataInvalidException,
  DataOutOfBoundException
} from "./exceptions";

type Line = Array<[x: number, y: number]>;

export type StatLines = {
  min: Line;
  medium: Line;
  max: Line;
};

export type FullStats = {
  height_cm: StatLines;
  weight_kg: StatLines;
  bmi: StatLines;
  wh: StatLines;
};

export function _evaluatePoly(line: Line, x: number): number {
  const first = line[0];
  const last = line.at(-1);
  if (!first || !last) {
    return NaN;
  }

  if (x < first[0] || x > last[0]) {
    return NaN;
  }

  let i;
  for (i = 0; i < line.length; i++) {
    const point = line[i];
    if (x == point[0]) {
      // Exact match
      return point[1];
    }
    if (point && x <= point[0]) break;
  }

  const current = line[i];
  const previous = line[i - 1];
  if (!current || !previous) {
    return NaN;
  }

  // i = the next indice (line[i-1] < x <= line[i])
  if (x === current[0]) return current[1];

  const xup = current[0];
  const yup = current[1];
  const xdw = previous[0];
  const ydw = previous[1];
  return ydw + (yup - ydw) * ((x - xdw) / (xup - xdw));
}

export function _stdDeviation(
  statLines: StatLines,
  x: number,
  y: number
): number {
  const avg = _evaluatePoly(statLines.medium, x);
  if (isNaN(avg)) {
    const first = statLines.medium[0];
    const last = statLines.medium.at(-1);
    throw new DataOutOfBoundException("value", x, [
      first?.[0] ?? NaN,
      last?.[0] ?? NaN
    ]);
  }
  if (y === avg) return 0;

  let ref;
  if (y < avg) {
    ref = _evaluatePoly(statLines.min, x);
  } else {
    ref = _evaluatePoly(statLines.max, x);
  }

  /* istanbul ignore next: this case is when the polynome is not fully completed */
  if (isNaN(ref)) {
    throw new DataOutOfBoundException("x", x);
  }

  const dev = Math.abs((avg - ref) / sigma);
  return (y - avg) / dev;
}

export function stdDeviationFor(
  sex: string | undefined,
  graphName: keyof FullStats,
  x?: number,
  y?: number
): number {
  if (!sex) {
    throw new DataInvalidException("sex", sex);
  }

  if (!(sex in amd_stats)) {
    throw new DataInvalidException("sex", sex);
  }

  if (!x) {
    throw new DataInvalidException("value.x", x);
  }

  if (!y) {
    throw new DataInvalidException("value.y", y);
  }

  const sexStr = sex as keyof typeof amd_stats;

  if (!(graphName in amd_stats[sexStr])) {
    throw new ConfigurationException("Unknown serie: " + graphName);
  }
  if (isNaN(x)) {
    throw new ConfigurationException(`Invalid x axis ${x}`);
  }
  if (isNaN(y)) {
    throw new ConfigurationException(`Invalid y axis ${y}`);
  }

  const graph = amd_stats[sexStr][graphName];
  if (!graph) {
    throw new ConfigurationException("Unknown serie: " + graphName);
  }

  return _stdDeviation(graph, x, y);
}

export const sigma = 1.64485; // 1.64485 = sigma at 90 for normal distribution
